import { DOBRO_CUSTOM_FIELDS, DOBROKOMUNITA_SLUG, SGN_SLUG } from '#shared/constants/communityProfile'
import type { Community, CommunityId, CommunityCustomField } from '#shared/types/Community'
import type { CommunityUserProfile, LocationData, UserProfile } from '#shared/types/User'
import { VisibilityType } from '#shared/types/visibility'

/**
 * Label of the built-in `location` field — the pin the member gets on the community map.
 * Kept next to the completeness rules so the form and the "Vyplň: ..." hints can't drift apart.
 */
export const COMMUNITY_LOCATION_LABEL = 'Súčasná lokalita - tu sa budeš zobrazovať na mape'
export const COMMUNITY_LOCATION_SHORT_LABEL = 'Súčasná lokalita'

/**
 * Custom fields a given community actually renders, in display order.
 *
 * Some communities hide fields that still live in their Firestore document, and a hidden field
 * must never be able to block joining — SGN's required "Do ktorého fóra patríš?" is exactly that.
 */
export function getVisibleCommunityCustomFields(community?: Community): CommunityCustomField[] {
    const customFields = community?.customFields
    if (!customFields) return []

    const fields = Object.values(customFields).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

    if (community?.slug === SGN_SLUG) {
        return fields.filter((field) => !field.label.includes('fóra patríš'))
    }

    if (community?.slug !== DOBROKOMUNITA_SLUG) {
        return fields
    }

    return fields.filter(
        (field) => field.name !== 'favoriteLocation' && !field.label.includes('Mesto alebo kraj, ku ktorému máš')
    )
}

/**
 * A community profile the member started but never finished. The wizard autosaves every
 * keystroke, so an entry alone does not mean "joined" — only pressing "Vstúpiť do komunity"
 * publishes it. Drafts are written as `VisibilityType.hidden` ('private'), which the Typesense
 * sync already keeps out of the members index.
 *
 * Deliberately compares against the enum value only: a handful of legacy rows store the literal
 * string 'hidden', and those are real members who hid themselves back when the UI allowed it.
 */
export function isCommunityJoinDraft(communityUserProfile?: CommunityUserProfile): boolean {
    return communityUserProfile?.visibility === VisibilityType.hidden
}

export function isCommunityMember(userProfile?: UserProfile, communityId?: CommunityId): boolean {
    if (!userProfile || !communityId) return false

    const communityUserProfile = userProfile.communityUserProfiles?.[communityId]
    return Boolean(communityUserProfile) && !isCommunityJoinDraft(communityUserProfile)
}

export function isCommunityLocationRequired(community?: Community): boolean {
    return Boolean(community?.isLocationRequired)
}

export function isCommunityStatusRequired(community?: Community): boolean {
    return Boolean(community?.isFilteredFeed) && community?.slug !== DOBROKOMUNITA_SLUG
}

function getCustomFieldsData(communityUserProfile?: CommunityUserProfile): Record<string, unknown> {
    const customFields = communityUserProfile?.customFields
    return customFields && typeof customFields === 'object' ? (customFields as Record<string, unknown>) : {}
}

function hasValue(value: unknown, field: CommunityCustomField): boolean {
    if (field.type === 'map') return Boolean((value as LocationData)?.name)
    if (Array.isArray(value)) return value.length > 0
    return Boolean(value)
}

/**
 * Option fields backed by a Typesense index (`options: { index, label }`) duplicate the built-in
 * "Rok, kedy si bol/a prijatý/á?" group picker, which writes `groupId` — not the field's own name.
 * BISLA has such a field flagged required, so enforcing it would block joining forever. Only
 * fields the form renders as a real, answerable input may hold a member back.
 */
export function isEnforceableCommunityField(field: CommunityCustomField): boolean {
    if (field.type === 'map') return true
    return Array.isArray(field.options)
}

export function hasDobroRepresentedOrganization(communityUserProfile?: CommunityUserProfile): boolean {
    const customFields = getCustomFieldsData(communityUserProfile)
    const selectedOrgIds = customFields[DOBRO_CUSTOM_FIELDS.representedOrganizations]
    const representedOrganizationText = customFields[DOBRO_CUSTOM_FIELDS.representedOrganizationText]

    if (Array.isArray(selectedOrgIds) && selectedOrgIds.length > 0) return true

    return typeof representedOrganizationText === 'string' && representedOrganizationText.trim().length > 0
}

export function hasDobroOrganizationTopics(communityUserProfile?: CommunityUserProfile): boolean {
    const topics = getCustomFieldsData(communityUserProfile)[DOBRO_CUSTOM_FIELDS.organizationTopics]
    return Array.isArray(topics) && topics.length > 0
}

/**
 * Labels of everything the member still has to fill in before they can join the community.
 * An empty result means the "Nastavenie v komunite" step is genuinely done.
 */
export function getMissingCommunityProfileFields(
    community?: Community,
    communityUserProfile?: CommunityUserProfile
): string[] {
    const missing: string[] = []

    for (const field of getVisibleCommunityCustomFields(community)) {
        if (!field.isRequired || !isEnforceableCommunityField(field)) continue

        const value = communityUserProfile?.[field.name as keyof CommunityUserProfile]
        if (!hasValue(value, field)) {
            missing.push(field.label)
        }
    }

    if (isCommunityLocationRequired(community) && !communityUserProfile?.location?.name) {
        missing.push(COMMUNITY_LOCATION_SHORT_LABEL)
    }

    if (isCommunityStatusRequired(community) && !communityUserProfile?.status) {
        missing.push('Status')
    }

    if (community?.slug === DOBROKOMUNITA_SLUG) {
        if (!hasDobroRepresentedOrganization(communityUserProfile)) missing.push('Reprezentovaná organizácia')
        if (!hasDobroOrganizationTopics(communityUserProfile)) missing.push('Kľúčové témy organizácie')
    }

    return missing
}
